import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, CloudOff, PackageOpen, ShieldAlert } from 'lucide-react';
import type { ScanCard } from '../../lib/receiving';
import { fmtDay, fmtTime } from '../../lib/receiving';
import { cn } from '../../utils/cn';

export type CardState =
  | { kind: 'IDLE' }
  | { kind: 'CARD'; card: ScanCard; fromQueue?: boolean }
  | { kind: 'QUEUED'; pending: number; by: string }
  | { kind: 'REFUSED'; message: string }
  | { kind: 'UNDONE'; card: ScanCard };

/**
 * Screen 2's destination card: one scan, one card, readable at arm's length. The slot is the
 * largest thing on the screen. Each outcome has its own colour, and none is styled as an
 * application error — a surplus or a damaged part is a normal day on the floor.
 */
export function DestinationCard({ state }: { state: CardState }) {
  const { t } = useTranslation();

  if (state.kind === 'IDLE') {
    return (
      <div className="flex min-h-[340px] flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center text-muted-foreground">
        <PackageOpen className="mb-3 h-12 w-12" />
        <p className="text-xl">{t('rcv.card.idle')}</p>
      </div>
    );
  }
  if (state.kind === 'QUEUED') {
    return (
      <div className="rounded-2xl border-2 border-sky-500/60 bg-sky-500/10 p-6">
        <div className="flex items-center gap-3 text-2xl font-semibold">
          <CloudOff className="h-8 w-8" /> {t('rcv.card.queuedTitle')}
        </div>
        <p className="mt-3 text-lg">{t('rcv.card.queued', { count: state.pending })}</p>
        <p className="mt-2 text-base text-muted-foreground">
          {t('rcv.card.scannedBy', { name: state.by, time: fmtTime(new Date().toISOString()) })}
        </p>
      </div>
    );
  }
  if (state.kind === 'REFUSED') {
    return (
      <div className="rounded-2xl border-2 border-destructive/50 bg-destructive/10 p-6">
        <div className="flex items-center gap-3 text-2xl font-semibold">
          <ShieldAlert className="h-8 w-8" /> {t('rcv.card.refusedTitle')}
        </div>
        <p className="mt-3 text-lg">{state.message}</p>
      </div>
    );
  }

  const c = state.card;
  const byline = (
    <p className="mt-4 text-base text-muted-foreground">
      {t('rcv.card.scannedBy', { name: c.by, time: fmtTime(c.ts) })}
      {state.kind === 'CARD' && state.fromQueue ? ` · ${t('rcv.card.sentWhenBack')}` : ''}
      {c.replayed ? ` · ${t('rcv.card.replayed')}` : ''}
    </p>
  );
  const part = c.product ? (
    <div className="text-xl font-semibold">
      {c.product.name} — {c.product.brand}
      <div className="text-base font-normal text-muted-foreground">
        {/* What was entered is an EAN only when it is digits; a reference typed from the part
            (supplier parts have no catalogue EAN) is shown as typed, not mislabelled as an EAN. */}
        {c.product.ean || /^\d{8,14}$/.test(c.code.trim())
          ? t('rcv.card.refLine', { ref: c.product.ref ?? '—', ean: c.product.ean ?? c.code })
          : t('rcv.card.refLineTyped', { ref: c.product.ref ?? '—', code: c.code })}
      </div>
    </div>
  ) : (
    <div className="text-xl font-semibold">{t('rcv.card.unknownCode', { code: c.code })}</div>
  );

  if (state.kind === 'UNDONE') {
    return (
      <div className="rounded-2xl border-2 bg-muted/40 p-6">
        {part}
        <p className="mt-3 text-2xl font-semibold">{t('rcv.card.undoneTitle')}</p>
        <p className="mt-1 text-lg">{t('rcv.card.undone', { qty: c.totalQty })}</p>
      </div>
    );
  }

  const zoneQ = (headline: string, detail: string) => (
    <div className="mt-4 flex flex-wrap items-center gap-6">
      <div className="rounded-xl bg-amber-500 px-6 py-2 text-[84px] font-black leading-none text-white dark:bg-amber-600">
        Q
      </div>
      <div className="min-w-0 text-2xl font-semibold">
        {headline}
        <div className="mt-1 text-lg font-normal">{detail}</div>
      </div>
    </div>
  );

  if (c.kind === 'UNKNOWN') {
    return (
      <div className="rounded-2xl border-2 border-amber-500/70 bg-amber-500/10 p-6">
        {part}
        {zoneQ(
          t('rcv.card.toQuarantine'),
          t('rcv.card.unknownTask', { ano: c.quarantine?.anomalies.join(', ') ?? '' })
        )}
        {byline}
      </div>
    );
  }
  if (c.kind === 'DAMAGED') {
    return (
      <div className="rounded-2xl border-2 border-amber-500/70 bg-amber-500/10 p-6">
        {part}
        <p className="mt-2 text-xl font-semibold">
          {t('rcv.card.damagedTitle', { qty: c.totalQty })}
        </p>
        {zoneQ(
          t('rcv.card.toQuarantine'),
          t('rcv.card.damagedTask', {
            ano: c.quarantine?.anomalies.join(', ') ?? '',
            still: c.stillExpected,
          })
        )}
        {byline}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-2xl border-2 p-5',
        c.allocations.length
          ? 'border-emerald-600/60 bg-emerald-500/5'
          : c.replenishment
            ? 'border-sky-600/60 bg-sky-500/5'
            : 'border-amber-500/70 bg-amber-500/10'
      )}
    >
      {part}
      {c.allocations.map((a, i) => (
        <div key={i} className={cn(i > 0 && 'mt-5 border-t-2 border-dashed pt-5')}>
          <div className="mt-3 flex flex-wrap items-center gap-6">
            <div
              className="rounded-xl bg-emerald-700 px-5 py-1 text-[84px] font-black leading-none tracking-tight text-white dark:bg-emerald-600"
              aria-label={t('rcv.card.slot')}
            >
              {a.slot}
            </div>
            <div className="min-w-0">
              <div className="text-2xl font-semibold">
                {a.customerName ?? t('rcv.walkIn')}{' '}
                <span className="ml-1 rounded-md bg-muted px-2 py-0.5 align-middle text-base font-medium">
                  {a.customerType === 'B2B' ? t('rcv.garage') : t('rcv.consumer')}
                </span>
              </div>
              <div className="text-xl">{t('rcv.card.order', { number: a.orderNumber })}</div>
            </div>
          </div>
          <dl className="mt-3 grid grid-cols-4 gap-x-4 gap-y-1 text-lg">
            <div>
              <dt className="text-sm text-muted-foreground">{t('rcv.card.putIn')}</dt>
              <dd className="font-semibold">{t('rcv.units', { count: a.qty })}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">{t('rcv.card.thisLine')}</dt>
              <dd className="font-semibold">
                {t('rcv.card.received', { n: a.lineReceived, of: a.lineQty })}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">{t('rcv.card.wholeOrder')}</dt>
              <dd className="font-semibold">
                {t('rcv.card.parts', { n: a.orderUnitsIn, of: a.orderUnitsTotal })}
              </dd>
            </div>
            {a.customerType === 'B2B' && a.bin && (
              <div>
                <dt className="text-sm text-muted-foreground">{t('rcv.card.garageBin')}</dt>
                <dd className="font-semibold">
                  {t('rcv.card.binProgress', {
                    n: a.bin.unitsIn,
                    of: a.bin.unitsDue,
                    count: a.bin.ordersDue,
                  })}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-muted-foreground">{t('rcv.card.promised')}</dt>
              <dd>{fmtDay(a.promisedAt)}</dd>
            </div>
            <div className="col-span-3">
              <dt className="text-sm text-muted-foreground">{t('rcv.card.placedBy')}</dt>
              <dd>
                {a.orderedBy} · {a.channelLabel}
              </dd>
            </div>
          </dl>
          {a.bin?.complete && (
            <div className="mt-3 flex items-center gap-3 rounded-xl bg-emerald-600 px-5 py-2 text-xl font-semibold text-white">
              <CheckCircle2 className="h-7 w-7" /> {t('rcv.card.binComplete', { slot: a.slot })}
            </div>
          )}
        </div>
      ))}
      {c.replenishment && (
        <div className={cn(c.allocations.length > 0 && 'mt-5 border-t-2 border-dashed pt-5')}>
          <div className="mt-3 flex flex-wrap items-center gap-6">
            <div className="rounded-xl bg-sky-700 px-5 py-1 text-[56px] font-black leading-none text-white dark:bg-sky-600">
              {c.replenishment.location ?? t('rcv.card.noLocation')}
            </div>
            <div className="text-2xl font-semibold">
              {t('rcv.card.replenishment')}
              <div className="text-lg font-normal">
                {t('rcv.card.putOnShelf', { count: c.replenishment.qty })}
              </div>
            </div>
          </div>
        </div>
      )}
      {c.quarantine && c.quarantine.reason === 'SURPLUS' && (
        <div
          className={cn(
            (c.allocations.length > 0 || c.replenishment) && 'mt-5 border-t-2 border-dashed pt-5'
          )}
        >
          <p className="mt-2 text-xl font-semibold">
            {t('rcv.card.nobodyWaits', { count: c.quarantine.qty })}
          </p>
          {zoneQ(
            t('rcv.card.toQuarantine'),
            t('rcv.card.surplusTask', { ano: c.quarantine.anomalies.join(', ') })
          )}
          {c.alreadyToday.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-500/60 bg-background p-4">
              <p className="flex items-center gap-2 text-lg font-semibold">
                <AlertTriangle className="h-5 w-5 text-amber-600" /> {t('rcv.card.countingTwice')}
              </p>
              <ul className="mt-2 space-y-1 text-base">
                {c.alreadyToday.map((x, i) => (
                  <li key={i}>
                    {fmtTime(x.ts)} · {x.qty} →{' '}
                    {x.destination === 'CUSTOMER' ? (
                      <>
                        <strong>{x.slot}</strong> {x.customerName},{' '}
                        {t('rcv.card.order', { number: x.orderNumber })}
                      </>
                    ) : (
                      t('rcv.card.replenishmentAt', { location: x.location ?? '—' })
                    )}
                    {' · '}
                    {t('rcv.by', { name: x.by })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <p className="mt-4 text-lg">
        <span className="text-muted-foreground">{t('rcv.card.stillExpected')}</span>{' '}
        <strong>{c.stillExpected}</strong>
      </p>
      {byline}
    </div>
  );
}
